<template>
    <div>
      <v-sheet tile height="56" class="d-flex align-center">
        <v-btn icon class="ma-2" @click="$refs.calendar.prev()">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-select
          v-model="type"
          :items="types"
          dense
          outlined
          hide-details
          class="ma-2"
          label="View"
          style="max-width: 140px"
        />

        <v-select
          v-model="weekday"
          :items="weekdays"
          dense
          outlined
          hide-details
          label="Weekdays"
          class="ma-2"
          style="max-width: 180px"
        />

        <v-spacer />

        <v-btn color="primary" class="ma-2" @click="startNewAppointment()">
          <v-icon left>mdi-plus</v-icon>
          Νέο Ραντεβού
        </v-btn>

        <v-btn icon class="ma-2" @click="$refs.calendar.next()">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-sheet>

      <v-sheet height="800">
        <v-calendar
          ref="calendar"
          v-model="value"
          :type="type"
          :weekdays="weekday"
          :events="events"
          :event-color="getEventColor"
          event-overlap-mode="column"
          :event-overlap-threshold="30"
          @change="getEvents"
          @click:time="intervalClick"
          @click:event="showEvent"
          @mousedown:event="startDrag"
          @mousemove:time="mouseMove"
          @mouseup:time="mouseUp"
        />

        <!-- Event details dialog -->
        <v-dialog
          v-model="selectedOpen"
          :close-on-content-click="false"
          :activator="selectedElement"
          max-width="620px"
        >
          <v-card color="grey lighten-4" flat>
            <v-toolbar :color="selectedEvent.color" dark>
              <v-toolbar-title v-html="selectedEvent.name" />
              <v-spacer />
              <!-- Edit / Save / Cancel buttons -->
              <v-btn
                icon
                v-if="!editMode"
                :disabled="!selectedEvent || editSaving || deleteLoading"
                @click="startEditSelected"
                :title="'Επεξεργασία'"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                v-if="editMode"
                :loading="editSaving"
                :disabled="editSaving"
                @click="saveEditSelected"
                :title="'Αποθήκευση'"
              >
                <v-icon>mdi-content-save</v-icon>
              </v-btn>
              <v-btn
                icon
                v-if="editMode"
                :disabled="editSaving"
                @click="cancelEditSelected"
                :title="'Ακύρωση αλλαγών'"
              >
                <v-icon>mdi-undo</v-icon>
              </v-btn>

              <!-- Delete button -->
              <v-btn
                icon
                color="error"
                :loading="deleteLoading"
                :disabled="editSaving || deleteLoading"
                @click="deleteSelected"
                :title="'Διαγραφή ραντεβού'"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>

              <!-- Close dialog -->
              <v-btn icon @click="closeSelectedDialog">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text>
              <div class="mb-2">
                <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                {{ formatEventRange(selectedEvent) }}
              </div>

              <!-- Reasons -->
              <div v-if="!editMode && selectedEvent.reasonsText" class="mb-2">
                <v-icon small class="mr-1">mdi-format-list-bulleted</v-icon>
                {{ selectedEvent.reasonsText }}
              </div>
              <div v-else-if="editMode" class="mb-2">
                <v-select
                  v-model="editSelectedReasons"
                  :items="reasons"
                  outlined
                  hide-details="auto"
                  label="Αιτίες"
                  prepend-icon="mdi-format-list-bulleted"
                  multiple
                  chips
                />
              </div>
              <div v-if="selectedVehicleDetails.owner" class="mb-2">
                <v-icon small class="mr-1">mdi-account</v-icon>
                <span v-if="selectedVehicleDetails.owner"><strong>Πελάτης:</strong> {{ selectedVehicleDetails.owner }}</span>
              </div>

              <!-- Vehicle info -->
              <div v-if="selectedVehicleDetails.hasAny" class="mb-2">
                <v-icon small class="mr-1">mdi-car-info</v-icon>
                <span v-if="selectedVehicleDetails.make"><strong>Μάρκα:</strong> {{ selectedVehicleDetails.make }}</span>
                <span v-if="selectedVehicleDetails.veh_type" class="ml-4"><strong>Type:</strong> {{ selectedVehicleDetails.veh_type }}</span>
                <span v-if="selectedVehicleDetails.vin" class="ml-4"><strong>VIN:</strong> {{ selectedVehicleDetails.vin }}</span>
                <div>
                <span v-if="selectedVehicleDetails.axle" class="ml-4"><strong>Διαμόρφωση αξόνων:</strong> {{ selectedVehicleDetails.axle }}</span>
              </div>
              </div>

              <!-- Notes -->
              <div v-if="!editMode && selectedEvent.details" class="prewrap">
                {{ selectedEvent.details }}
              </div>
              <div v-else-if="editMode">
                <v-textarea
                  label="Σχόλια"
                  rows="3"
                  v-model="editNotes"
                  hint="Προαιρετικά"
                  auto-grow
                  outlined
                />
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text color="secondary" @click="selectedOpen = false">
                Κλείσιμο
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Create appointment dialog -->
        <v-dialog v-model="dialog" persistent max-width="760px">
          <v-card>
            <v-card-title>
              <span class="text-h5">Νέο Ραντεβού</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <!-- Smart plate selection -->
                  <v-col cols="12" sm="8">
                    <v-autocomplete
                      v-model="selectedVehicle"
                      :items="vehicles"
                      :loading="vehiclesLoading"
                      return-object
                      clearable
                      hide-details="auto"
                      label="Αναζήτηση πινακίδας"
                      prepend-icon="mdi-car"
                      :filter="vehicleFilter"
                      @change="onVehicleSelected"
                    >
                      <template v-slot:item="{ item }">
                        <div class="d-flex flex-column">
                          <div>
                            <strong>{{ item.PlateNumber }}</strong>
                            <span v-if="item.Owner && item.Owner.Name"> — {{ item.Owner.Name }}</span>
                          </div>
                          <div class="text--secondary" style="font-size: 0.8em;">
                            <span v-if="item.Make">{{ item.Make }}</span>
                            <span v-if="item.Vin"> — {{ item.Vin }}</span>
                          </div>
                        </div>
                      </template>
                      <template v-slot:selection="{ item }">
                        <span><strong>{{ item.PlateNumber }}</strong><span v-if="item.Owner && item.Owner.Name"> — {{ item.Owner.Name }}</span></span>
                      </template>
                    </v-autocomplete>
                  </v-col>

                  <!-- Reasons multi-select (bit flags) -->
                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="selectedReasons"
                      :items="reasons"
                      hide-details="auto"
                      label="Αιτίες"
                      prepend-icon="mdi-format-list-bulleted"
                      multiple
                      chips
                    />
                  </v-col>

                  <!-- Date picker -->
                  <v-col cols="12" sm="4">
                    <v-menu
                      v-model="dateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-bind="attrs"
                          v-on="on"
                          label="Ημερομηνία"
                          prepend-icon="mdi-calendar"
                          readonly
                          :value="datePart"
                          dense
                          outlined
                          hide-details="auto"
                        />
                      </template>
                      <v-date-picker
                        v-model="datePart"
                        @input="dateMenu = false; syncDateTimeFromParts()"
                      />
                    </v-menu>
                  </v-col>

                  <!-- Time picker -->
                  <v-col cols="12" sm="4">
                    <v-menu
                      v-model="timeMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-bind="attrs"
                          v-on="on"
                          label="Ώρα"
                          prepend-icon="mdi-clock-outline"
                          readonly
                          :value="timePart"
                          dense
                          outlined
                          hide-details="auto"
                        />
                      </template>
                      <v-time-picker
                        v-model="timePart"
                        :allowed-minutes="allowedStep"
                        format="24hr"
                        min="08:30"
                        max="17:00"
                        @input="timeMenu = false; syncDateTimeFromParts()"
                      />
                    </v-menu>
                  </v-col>

                  <!-- Notes (optional) -->
                  <v-col cols="12" sm="12">
                    <v-textarea
                      label="Σχόλια"
                      rows="3"
                      v-model="appointmentForm.Notes"
                      hint="Προαιρετικά"
                      auto-grow
                      outlined
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn color="blue darken-1" text @click="dialog = false">
                Ακύρωση
              </v-btn>
              <v-btn color="blue darken-1" text :disabled="!canSave" @click="createAppointment">
                Αποθήκευση
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-sheet>
    </div>
  </template>

  <script>

  export default {
    name: 'Appointments',
    data: () => ({
      // Calendar state
      value: '',
      type: 'week',
      types: ['month', 'week', 'day'],
      weekday: [1, 2, 3, 4, 5, 6, 0],
      weekdays: [
        { text: 'Sun - Sat', value: [0, 1, 2, 3, 4, 5, 6] },
        { text: 'Mon - Sun', value: [1, 2, 3, 4, 5, 6, 0] },
        { text: 'Mon - Fri', value: [1, 2, 3, 4, 5] },
      ],

      // Events
      events: [],
      eventDefaultColor: 'indigo',

      // Event details dialog
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,

      // Inline edit/delete state for selected event
      editMode: false,
      editNotes: '',
      editSelectedReasons: [],
      editSaving: false,
      deleteLoading: false,

      // Vehicles/autocomplete
      vehicles: [],
      vehiclesLoading: false,
      selectedVehicle: null,

      // Create dialog/form
      dialog: false,
      dateMenu: false,
      timeMenu: false,
      datePart: null, // 'YYYY-MM-DD'
      timePart: null, // 'HH:mm'
      appointmentForm: {
        DateTime: null,   // epoch ms
        VehId: null,      // vehicle id
        Notes: null,      // optional
      },
      // Reasons multi-select (bit flags)
      selectedReasons: [], // array of numeric bit values selected
      reasons: [
        { text: 'Service', value: 1 },
        { text: 'Βαλβίδες', value: 2 },
        { text: 'Φρένα', value: 4 },
        { text: 'Κομπρεσέρ/Ξηραντής', value: 8 },
        { text: 'Αερόφουσκα', value: 16 },
        { text: 'Συμπλέκτες', value: 32 },
        { text: 'Ανάρτηση', value: 64 },
        { text: 'Διάγνωση', value: 128 },
        { text: 'Άλλο', value: 256 },
      ],

      defaultDurationMs: 60 * 60 * 1000,

      // Drag state
      dragging: false,
      dragEvent: null,
      dragOriginalStart: null,
    }),

    computed: {
      // user must select datetime, vehicle and at least one reason (bit-flag > 0)
      canSave() {
        return !!this.appointmentForm.DateTime && !!this.selectedVehicle && this.reasonFlags > 0;
      },
      // Compute the integer bit flags from the selectedReasons array
      reasonFlags() {
        return (this.selectedReasons || []).reduce((acc, v) => acc | v, 0);
      },

      // Safely extract vehicle details for the selected event
      selectedVehicleDetails() {
        const ev = this.selectedEvent || {};
        const app = ev.app || {};
        // Prefer nested Vehicle object if present
        const v = app.Vehicle || null;

        // Try a few common casings/aliases just in case
        const make = (v && (v.Make || v.make)) || null;
        const owner = (v && v.Owner.Name) || null;
        const veh_type = (v && v.VehType) || null;
        const vin = (v && (v.Vin || v.vin)) || null;
        const axleRaw =
          (v && (v.AxleConfiguration || v.AxleConfig || v.AxleConf || v.Axles || v.axles || v.axleConfiguration)) ||
          (app && (app.AxleConfiguration || app.AxleConfig || app.Axles)) ||
          null;

        // Optional normalization for axle config (keep as-is if unknown type)
        const axle =
          typeof axleRaw === 'number' ? String(axleRaw) : (axleRaw || null);

        return {
          make,
          owner,
          vin,
          veh_type,
          axle,
          get hasAny() {
            return !!(this.make || this.vin || this.axle);
          },
        };
      },
    },

    async created() {
      await this.fetchVehicles();
    },

    methods: {
      // Vehicles
      async fetchVehicles() {
        try {
          this.vehiclesLoading = true;
          const response = await this.$api.get('/repairs/vehicles');
          this.vehicles = response.data || [];
        } finally {
          this.vehiclesLoading = false;
        }
      },
      vehicleFilter(item, queryText) {
        const q = (queryText || '').toString().toLowerCase();
        const plate = (item.PlateNumber || '').toLowerCase();
        const owner = (item.Owner && item.Owner.Name ? item.Owner.Name : '').toLowerCase();
        const make = (item.Make || '').toLowerCase();
        const vin = (item.Vin || '').toLowerCase();
        return plate.includes(q) || owner.includes(q) || make.includes(q) || vin.includes(q);
      },
      onVehicleSelected(v) {
        this.appointmentForm.VehId = v ? (v.Id || null) : null;
      },

      // Calendar events loader
      async getEvents({ start, end }) {
        try {
          const response = await this.$api.get('/repairs/appointments/list', {
            params: {
              'start-date': start.date,
              'end-date': end.date,
            },
          });
          const items = response.data || [];
          this.events = items.map((a) => {
            const raw = a.DateTime || a.Date;
            const d = new Date(raw);
            const startMs = d.getTime() + d.getTimezoneOffset() * 60000
            const plate =
              (a.Vehicle && a.Vehicle.PlateNumber) ||
              a.PlateNumber ||
              'Ραντεβού';
            const reasonsText = this.getReasonLabelsFromFlags(a.Reason).join(', ');
            const name = `${plate}${reasonsText ? ' — ' + reasonsText : ''}`;
            return {
              name,
              start: startMs,
              end: startMs + this.defaultDurationMs,
              color: this.eventDefaultColor,
              timed: true,
              details: a.Notes,     // using Notes field
              reasonFlags: a.Reason || 0,
              reasonsText,
              app: a,               // keep original object with Id for PATCH and vehicle info
            };
          });
        } catch {
          // Optional: add snackbar/toast
        }
      },

      // Helper to convert integer reason flags to labels array
      getReasonLabelsFromFlags(flags) {
        if (!flags || typeof flags !== 'number') return [];
        return this.reasons
          .filter((r) => (flags & r.value) === r.value)
          .map((r) => r.text);
      },

      // Convert flags -> array of values for multi-select
      flagsToArray(flags) {
        if (!flags || typeof flags !== 'number') return [];
        return this.reasons
          .filter((r) => (flags & r.value) === r.value)
          .map((r) => r.value);
      },

      // Helpers
      getEventColor(event) {
        return event.color || this.eventDefaultColor;
      },

      showEvent({ nativeEvent, event }) {
        const open = () => {
          this.selectedEvent = event;
          this.selectedElement = nativeEvent.target;
          // Prepare edit fields from selected
          this.prepareEditFromSelected();
          requestAnimationFrame(() =>
            requestAnimationFrame(() => (this.selectedOpen = true))
          );
        };

        if (this.selectedOpen) {
          this.selectedOpen = false;
          requestAnimationFrame(() => requestAnimationFrame(() => open()));
        } else {
          open();
        }

        nativeEvent.stopPropagation();
      },

      formatEventRange(event) {
        try {
          if (!event || !event.start) return '';
          const optionsDate = { year: 'numeric', month: '2-digit', day: '2-digit' };
          const optionsTime = { hour: '2-digit', minute: '2-digit' };
          const start = new Date(event.start);
          const end = new Date(event.end || event.start + this.defaultDurationMs);
          return `${start.toLocaleDateString(undefined, optionsDate)} ${start.toLocaleTimeString(undefined, optionsTime)} - ${end.toLocaleTimeString(undefined, optionsTime)}`;
        } catch {
          return '';
        }
      },

      // Opening create dialog with current time (respecting business rules)
      startNewAppointment() {
        const nowRounded = this.clampAndSnapToBusinessHours(Date.now());
        this.setFormDateTime(nowRounded);
        this.resetFormButKeepDate();
        this.dialog = true;
      },

      // Clicking on interval opens dialog with prefilled time (respecting business rules)
      intervalClick(tms) {
        const start = this.clampAndSnapToBusinessHours(this.toTime(tms));
        this.setFormDateTime(start);
        this.resetFormButKeepDate();
        this.dialog = true;
      },

      // Date/time conversions
      toTime(tms) {
        return new Date(tms.year, tms.month - 1, tms.day, tms.hour, tms.minute).getTime();
      },
      roundTime(time, down = true) {
        const roundTo = 10; // minutes
        const roundMs = roundTo * 60 * 1000;
        const delta = time % roundMs;
        return down
          ? time - delta
          : time + ((roundMs - delta) % roundMs);
      },
      clampAndSnapToBusinessHours(ts) {
        const d = new Date(ts);
        const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 8, 30, 0, 0).getTime();
        const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 17, 0, 0, 0).getTime();
        let t = this.roundTime(ts, true);
        if (t < start) t = start;
        if (t > end) t = end;
        return t;
      },

      allowedStep: m => m % 10 === 0,

      // Sync helpers for date/time picker parts and timestamp
      setFormDateTime(ts) {
        this.appointmentForm.DateTime = ts;
        const d = new Date(ts);
        this.datePart = d.toISOString().substr(0, 10);
        this.timePart = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      },
      syncDateTimeFromParts() {
        if (!this.datePart || !this.timePart) return;
        const [h, m] = this.timePart.split(':').map((x) => parseInt(x, 10));
        const [year, month, day] = this.datePart.split('-').map((x) => parseInt(x, 10));
        const dt = new Date(year, month - 1, day, h, m, 0, 0).getTime();
        this.appointmentForm.DateTime = this.clampAndSnapToBusinessHours(dt);
      },

      resetFormButKeepDate() {
        this.selectedVehicle = null;
        this.appointmentForm.VehId = null;
        this.appointmentForm.Notes = null;
        this.selectedReasons = [];
        // Keep DateTime, datePart, timePart as set
      },

      // Format timestamp to ISO8601 with local timezone offset, e.g. "2025-03-18T09:00:00+02:00"
      formatIsoWithOffset(ts) {
        if (!ts && ts !== 0) return null;
        const d = new Date(ts);
        const pad = (n, w = 2) => String(n).padStart(w, '0');
        const yyyy = d.getFullYear();
        const MM = pad(d.getMonth() + 1);
        const dd = pad(d.getDate());
        const HH = pad(d.getHours());
        const mm = pad(d.getMinutes());
        const ss = pad(d.getSeconds());
        //const ms = pad(d.getMilliseconds(), 3);
        //const tz = d.getTimezoneOffset(); // minutes behind UTC; negative for UTC+X
        //const sign = tz <= 0 ? '+' : '-';
        //const abs = Math.abs(tz);
        //const tzh = pad(Math.floor(abs / 60));
        //const tzm = pad(abs % 60);
        // Include milliseconds for precision; remove ".{ms}" if not desired
        return `${yyyy}-${MM}-${dd}T${HH}:${mm}:${ss}`;//${sign}${tzh}:${tzm}`;
      },

      // Create appointment (POST)
      async createAppointment() {
        try {
          const iso = this.formatIsoWithOffset(this.appointmentForm.DateTime);

          const payload = {
            Date: iso,
            VehId: this.appointmentForm.VehId || (this.selectedVehicle && this.selectedVehicle.Id) || null,
            // Send integer bit flags in Reason
            Reason: this.reasonFlags,
            Notes: this.appointmentForm.Notes || null,
          };

          const response = await this.$api.post('/repairs/appointments/create', payload);
          if (response && (response.status === 201 || response.status === 200)) {
            const saved = response.data || {};
            const startIso = saved.DateTime || saved.Date || payload.DateTime || payload.Date;
            const start = startIso ? new Date(startIso).getTime() : this.appointmentForm.DateTime;

            const plate =
              (this.selectedVehicle && this.selectedVehicle.PlateNumber) ||
              (saved.Vehicle && saved.Vehicle.PlateNumber) ||
              saved.PlateNumber ||
              'Ραντεβού';

            const reasonsText =
              this.getReasonLabelsFromFlags(saved.Reason).join(', ') ||
              this.getReasonLabelsFromFlags(this.reasonFlags).join(', ');

            const name = `${plate}${reasonsText ? ' — ' + reasonsText : ''}`;

            this.events.push({
              name,
              start,
              end: start + this.defaultDurationMs,
              color: this.eventDefaultColor,
              timed: true,
              details: saved.Notes || payload.Notes || null,
              reasonFlags: saved.Reason || this.reasonFlags || 0,
              reasonsText,
              app: saved,
            });
            this.dialog = false;
          }
        } catch {
          // Optional: add error feedback
        }
      },

      // Prepare edit fields from the currently selected event
      prepareEditFromSelected() {
        const ev = this.selectedEvent || {};
        const app = ev.app || {};
        const flags = typeof ev.reasonFlags === 'number'
          ? ev.reasonFlags
          : (typeof app.Reason === 'number' ? app.Reason : 0);
        this.editSelectedReasons = this.flagsToArray(flags);
        this.editNotes = ev.details || app.Notes || '';
        this.editMode = false;
      },

      // Edit actions
      startEditSelected() {
        this.editMode = true;
      },
      cancelEditSelected() {
        // Revert edits from original selected event
        this.prepareEditFromSelected();
      },
      async saveEditSelected() {
        const ev = this.selectedEvent || {};
        const app = ev.app || {};
        const id = (app && (app.Id || app.id)) || null;
        if (!id) return;

        const newFlags = (this.editSelectedReasons || []).reduce((acc, v) => acc | v, 0);

        this.editSaving = true;
        try {
          const body = {
            Reason: newFlags,
            Notes: this.editNotes || null,
          };
          const resp = await this.$api.patch(`/repairs/appointments/${id}`, body);
          if (resp && (resp.status === 200 || resp.status === 202 || resp.status === 204)) {
            // Update local event object (selectedEvent is a reference to events array item)
            const reasonsText = this.getReasonLabelsFromFlags(newFlags).join(', ');
            ev.reasonFlags = newFlags;
            ev.reasonsText = reasonsText;
            ev.details = this.editNotes || null;

            if (ev.app) {
              ev.app.Reason = newFlags;
              ev.app.Notes = this.editNotes || null;
            }

            // Rebuild title with plate + reasons
            const plate =
              (ev.app && ev.app.Vehicle && ev.app.Vehicle.PlateNumber) ||
              (ev.app && ev.app.PlateNumber) ||
              (ev.name ? ev.name.split(' — ')[0] : 'Ραντεβού');
            ev.name = `${plate}${reasonsText ? ' — ' + reasonsText : ''}`;

            this.editMode = false;
          }
        } catch {
          // Optional: show feedback
        } finally {
          this.editSaving = false;
        }
      },

      // Delete selected appointment
      async deleteSelected() {
        const ev = this.selectedEvent || {};
        const app = ev.app || {};
        const id = (app && (app.Id || app.id)) || null;
        if (!id) return;

        this.deleteLoading = true;
        try {
          const resp = await this.$api.delete(`/repairs/appointments/${id}`);
          if (resp && (resp.status === 200 || resp.status === 204)) {
            const idx = this.events.indexOf(ev);
            if (idx !== -1) this.events.splice(idx, 1);
            this.selectedOpen = false;
            this.editMode = false;
          }
        } catch {
          // Optional: show feedback
        } finally {
          this.deleteLoading = false;
        }
      },

      closeSelectedDialog() {
        this.selectedOpen = false;
        this.editMode = false;
      },

      // Drag & drop for events
      startDrag({ nativeEvent, event }) {
        if (!event || !event.app) return;
        this.dragging = true;
        this.dragEvent = event;
        this.dragOriginalStart = event.start;
        nativeEvent.stopPropagation();
        nativeEvent.preventDefault();
      },
      mouseMove(tms) {
        if (!this.dragging || !this.dragEvent) return;
        const ts = this.clampAndSnapToBusinessHours(this.toTime(tms));
        // Optimistic visual update while dragging
        this.dragEvent.start = ts;
        this.dragEvent.end = ts + this.defaultDurationMs;
      },
      async mouseUp(tms) {
        if (!this.dragging || !this.dragEvent) return;
        const newTs = this.clampAndSnapToBusinessHours(this.toTime(tms));
        const ev = this.dragEvent;
        const oldTs = this.dragOriginalStart;
        this.dragging = false;
        this.dragEvent = null;
        this.dragOriginalStart = null;

        // If unchanged after snapping/clamping, do nothing
        if (newTs === oldTs) return;

        // Persist move via PATCH
        const id = (ev.app && (ev.app.Id || ev.app.id)) || null;
        if (!id) return;

        try {
          const iso = this.formatIsoWithOffset(newTs);
          const patchBody = { Date: iso }; // include both keys for compatibility
          const resp = await this.$api.patch(`/repairs/appointments/${id}`, patchBody);
          // If PATCH fails status-wise, revert
          if (!(resp && (resp.status === 200 || resp.status === 202 || resp.status === 204))) {
            ev.start = oldTs;
            ev.end = oldTs + this.defaultDurationMs;
          }
        } catch {
          // Revert on error
          ev.start = oldTs;
          ev.end = oldTs + this.defaultDurationMs;
        }
      },
    },
  };
  </script>

  <style scoped>
  .toupper input {
    text-transform: uppercase;
  }
  .prewrap {
    white-space: pre-wrap;
  }
  </style>